<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>

<script setup lang="ts">
import { authService } from '~/services/api/auth'

// Set default meta tags
useHead({
  title: 'bLink - Bike Sharing Service',
  meta: [
    { name: 'description', content: 'Eco-friendly bike sharing service in Kobe' }
  ]
})

// Language persistence/restoration is handled centrally in `plugins/i18n.ts`.

// Watch for when the auth store becomes available (client only — no Pinia during SSR here)
watchEffect(async () => {
  if (import.meta.server) {
    return
  }
  try {
    const authStore = resolveAuthStore()
    if (!authStore) {
      return
    }

    // Handle auth token refresh
    if (authStore.token) {
      const decoded = authService.parseToken(authStore.token)
      const exp = decoded.exp * 1000 // Convert to milliseconds
      const now = Date.now()
      const timeToExpiry = exp - now
      
      // If token expires in less than a day, refresh it
      if (timeToExpiry < 24 * 60 * 60 * 1000) {
        try {
          await authService.refreshToken(authStore.token)
        } catch (error) {
          console.error('Failed to refresh token:', error)
        }
      }
    }
  } catch (error) {
    // Store not ready yet, this is expected during initialization
    // console.log('Auth store not ready yet:', error.message)
  }
})
</script>

<style>
.page-enter-active,
.page-leave-active {
  transition: all 0.15s ease-out;
}
.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Global dropdown light styling */
.ui-dropdown {
  background-color: white !important;
  border-color: #e5e7eb !important;
}

.ui-dropdown-item {
  background-color: white !important;
  color: #374151 !important;
}

.ui-dropdown-item:hover {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}

.ui-dropdown-item.active {
  background-color: #ecfdf5 !important;
  color: #059669 !important;
}

.ui-dropdown-container {
  background-color: white !important;
  border-color: #e5e7eb !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

/* Additional dropdown overrides */
[data-popper-placement] {
  background-color: white !important;
  border-color: #e5e7eb !important;
}

[data-popper-placement] * {
  background-color: white !important;
  color: #374151 !important;
}

[data-popper-placement] *:hover {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}

/* Force light styling for Nuxt UI buttons (ghost/soft variants) */
.ui-button,
button[class*="ghost"],
.dark .ui-button,
.dark button[class*="ghost"] {
  color-scheme: light;
}

.ui-button:hover,
button[class*="ghost"]:hover {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}

.ui-button[data-variant="ghost"][data-color="emerald"]:hover,
.ui-button[data-color="emerald"][data-variant="ghost"]:hover {
  background-color: #ecfdf5 !important;
  color: #047857 !important;
}

.ui-button[data-variant="ghost"][data-color="red"]:hover,
.ui-button[data-variant="ghost"][data-color="red"]:hover {
  background-color: #fef2f2 !important;
  color: #b91c1c !important;
}

.ui-button[data-variant="soft"][data-color="emerald"]:hover {
  background-color: #ecfdf5 !important;
  color: #047857 !important;
}

/* Keep native selects light (avoids dark OS/browser hover backgrounds) */
html {
  color-scheme: light;
}

select {
  color-scheme: light;
  background-color: #ffffff !important;
  color: #374151 !important;
}

select:hover,
select:focus {
  background-color: #f9fafb !important;
  color: #374151 !important;
}

select option {
  background-color: #ffffff !important;
  color: #374151 !important;
}

html.dark select,
.dark select {
  background-color: #ffffff !important;
  color: #374151 !important;
}

html.dark select:hover,
html.dark select:focus,
.dark select:hover,
.dark select:focus {
  background-color: #f9fafb !important;
  color: #374151 !important;
}
</style>
